import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useContext,
} from "react";
import ReactCrop from "react-image-crop";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { ReactComponent as UploadIcon } from "../Images/UploadDoc.svg";
import "react-image-crop/dist/ReactCrop.css";
import { Box, Button, Grid } from "@material-ui/core";
import { canvasToBlob } from "blob-util";
import { useDispatch, useSelector } from "react-redux";
import { showSnackbar } from "./Snackbar/SnackbarActions";
import CloseIcon from "@material-ui/icons/Close";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import { IconButton, CircularProgress } from "@material-ui/core";
import { useParams } from "react-router";
import DataService from "../Service";
import { UploadFilesComponentContext } from "./FormGenerator/context/UploadFilesComponentContext";

function generateDownload(canvas: any, crop: any) {
  if (!crop || !canvas) {
    return;
  }

  canvas.toBlob(
    (blob: any) => {
      const previewUrl = window.URL.createObjectURL(blob);

      const anchor = document.createElement("a");
      anchor.download = "cropPreview.png";
      anchor.href = URL.createObjectURL(blob);
      anchor.click();

      window.URL.revokeObjectURL(previewUrl);
    },
    "image/png",
    1
  );
}

export default function ImageCrop(props: any) {
  const classes = useStyles();

  const params = useParams();
  const {
    existingImage,
    setImageBlob,
    label,
    allowExtension,
    section,
    imgPreviewProps,
    cropProps,
    previewStyle,
    disabled,
  } = props.componentObject;
  //Accessing state from Redux
  const OrgId = useSelector(
    //   @ts-ignore
    (state) => state.organisationDetailsReducer.data.id
  );

  const { fileDataState, fetchedFilesState } = useContext(
    UploadFilesComponentContext
  );

  //   @ts-ignore
  const employeeId = useSelector((state) => state.contactAreaReducer.data.id);

  const { fetchedFiles, setFetchedFiles } = fetchedFilesState;

  const [upImg, setUpImg]: any = useState();
  const imgRef = useRef(null);
  const dispatch = useDispatch();
  const previewCanvasRef = useRef(null);
  const [crop, setCrop]: any = useState(
    cropProps ? cropProps : { unit: "%", width: 30, aspect: 1 }
  );
  const [completedCrop, setCompletedCrop] = useState(null);
  const [isDeletingFile, setIsDeletingFile] = useState(false);

  const fileFormats = allowExtension?.length
    ? allowExtension
    : ["jpg", "png", "jpeg", "PNG", "JPEG", "JPG"];

  // removing the files from the server
  const fileRemoveFromTheServer = (fileId: any) => {
    let url = employeeId
      ? `/employees/delete_file?employee_id=${
          // @ts-ignore
          params.employeeid ? params.employeeid : employeeId
        }&file_id=${fileId}`
      : `/organisations/delete_file?organisation_id=${
          // @ts-ignore
          params.id ? params.id : OrgId
        }&file_id=${fileId}`;

    setIsDeletingFile(true);
    DataService.deleteDirectFeild(url)
      .then((res) => {
        setIsDeletingFile(false);
        dispatch(showSnackbar("success", ["File was deleted successfully!"]));

        // updating context after deleting file from the server
        setFetchedFiles((prevState: any) => {
          return prevState.filter((item: any) => item.id !== fileId);
        });
        setImageBlob(null);
      })
      .catch((err) => {
        setIsDeletingFile(false);
        dispatch(showSnackbar("error", ["Something went wrong"]));
      });
  };

  const onSelectFile = (e: any) => {
    if (!e?.target?.files) {
      return;
    }

    const splited = e?.target?.files[0]?.name.split(".");
    if (!fileFormats.includes(splited[splited.length - 1])) {
      return dispatch(showSnackbar("error", ["Unsupported file format!"]));
    }

    if (e?.target?.files[0]?.size > 6000000) {
      return dispatch(
        showSnackbar("error", [
          "Image file size too large, reduce size below 6MB and try again!",
        ])
      );
    }

    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener("load", () => setUpImg(reader.result));
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onLoad = useCallback((img) => {
    imgRef.current = img;
  }, []);

  useEffect(() => {
    if (previewCanvasRef?.current) {
      const canvas: any = previewCanvasRef.current;
      canvas.toDataURL("image/png");
      canvas.toBlob(async (blob: any) => {
        setImageBlob && setImageBlob(await canvasToBlob(canvas));
      }, "image/png");
    }
  }, [previewCanvasRef?.current, crop, completedCrop]);

  useEffect(() => {
    if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
      return;
    }

    const image: any = imgRef.current;
    const canvas: any = previewCanvasRef.current;
    const crop: any = completedCrop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext("2d");
    const pixelRatio = window.devicePixelRatio;

    canvas.width = crop.width * pixelRatio;
    canvas.height = crop.height * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );
  }, [completedCrop]);

  return (
    <Grid container className={classes.root}>
      {label && (
        <Grid item xs={12} md={4} style={{}}>
          {label}
          &nbsp;
        </Grid>
      )}
      <Grid item xs={12} md={8}>
        <div>
          <div>
            <input
              className={classes.input}
              id="upload-file"
              name="profile_image"
              type="file"
              accept="image/*"
              onChange={onSelectFile}
              disabled={disabled}
            />
            <label htmlFor="upload-file">
              <Button
                variant="contained"
                className={classes.uploadFile}
                component="span"
                startIcon={<UploadIcon />}
                disabled={disabled}
              >
                {"Upload Image"}
              </Button>
            </label>
          </div>
          <div>
            {upImg && (
              <Box mt={2} className={classes.cropArea}>
                <ReactCrop
                  src={upImg}
                  onImageLoaded={onLoad}
                  crop={crop}
                  onChange={(c: any) => setCrop(c)}
                  onComplete={(c: any) => setCompletedCrop(c)}
                />
              </Box>
            )}
            <div>
              {upImg && (
                <canvas
                  ref={previewCanvasRef}
                  // Rounding is important so the canvas width and height matches/is a multiple for sharpness.
                  style={previewStyle}
                  className={classes.canvasStyle}
                />
              )}
              {!upImg && existingImage && (
                <Box display="flex" alignItems="center">
                  {existingImage.url && (
                    <>
                      {" "}
                      <img
                        className={classes.existingImage}
                        src={existingImage.url}
                        alt="No Image"
                        width="100"
                        height="100"
                        {...imgPreviewProps}
                      />
                      {isDeletingFile ? (
                        <CircularProgress size={15} />
                      ) : (
                        <AddCircleIcon
                          type="submit"
                          onClick={() =>
                            fileRemoveFromTheServer(existingImage.id)
                          }
                          fontSize="large"
                          color="error"
                          style={{
                            cursor: "pointer",
                            transform: "rotate(45deg)",
                          }}
                        />
                      )}
                    </>
                  )}
                </Box>
              )}
            </div>
          </div>
        </div>
      </Grid>
    </Grid>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    marginBottom: "20px",
  },
  input: {
    display: "none",
  },
  canvasStyle: {
    width: 200,
    height: 200,
    borderRadius: "50%",
  },
  uploadFile: {
    backgroundColor: "#EF7000",
    color: "white",
    "&:hover": {
      backgroundColor: "#EF7000",
    },
  },
  cropArea: {
    paddingTop: "10px",
  },
  existingImage: {
    borderRadius: "50%",
    marginTop: "10px",
    marginRight: "5px",
    width: "250px",
  },
}));
